import Quagga, {
  QuaggaJSConfigObject,
  QuaggaJSResultObject,
} from '@ericblade/quagga2';
import { logSentry } from 'app/core/errors/error-utils';
import styledMixins from 'app/styles/styledMixins';
import {
  Dims,
  Opt,
  QuaggaBarcodeType,
} from 'app/tools/general.types';
import { LSKey } from 'app/tools/hooks/local-storage/local-storage.types';
import useLocalRef from 'app/tools/hooks/local-storage/use-local-ref';
import {
  useComponentIsStillLoaded,
  useEffectOnce,
  useTimeMountedExceeds,
} from 'app/tools/hooks/misc-hooks';
import _ from 'lodash';
import { useSubscription } from 'observable-hooks';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { NEVER, Observable } from 'rxjs';
import styled from 'styled-components/macro';
import CameraLoading from './CameraLoading';
import initializeQuagga from './utilities/initializeQuagga';
import {
  CodeRead,
  getOffsets,
  getScanOffsetsFromTargetDims,
  getTargetDims,
  quaggaResultIsValid,
  WindowDimensions,
} from './utilities/scan-utils';

const TargetDiv = styled.div<{ $size: Dims; $ready: boolean }>`
  &,
  & > video {
    width: ${p => p.$size.width}px;
    height: ${p => p.$size.height}px;
    transition: opacity 0.3s;
    opacity: ${p => (p.$ready ? 1 : 0)};
    object-fit: cover;
    // NOTE: this stops the ghost-space appearing at the bottom
    // of the page that was making scrollbars appear
    vertical-align: bottom;
  }
  & > canvas {
    display: none;
  }
`;

type ScanLineOpts = { $scanWSize: number };
const RedScanLine = styled.div.attrs({
  children: <div />,
})<ScanLineOpts>`
  position: absolute;
  ${styledMixins.fullsize}
  ${styledMixins.flexCenter}
  > div {
    height: 3px;
    border-radius: 1.5px;
    width: ${p => p.$scanWSize * 0.8}px;
    background-color: white;
  }
`;

export type VideoCommand = 'play' | 'pause';

type Props = {
  className?: string;
  onDataUpdate: (result: Opt<CodeRead>, err?: Error) => unknown;
  onStreamError?: (error: unknown) => void;
  scanOptions?: {
    barcodeTypes?: QuaggaBarcodeType[];
  };
  windowDimensions: WindowDimensions;
  command$?: Observable<VideoCommand>;
};

export default function Scanner({
  className,
  onDataUpdate,
  onStreamError,
  scanOptions: { barcodeTypes = [QuaggaBarcodeType.CODE_128] } = {},
  windowDimensions,
  command$ = NEVER,
}: Props): JSX.Element {
  const targetRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [slowLoading] = useTimeMountedExceeds(1200);
  const [loading, setLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);

  const windowDimsRef = useRef(windowDimensions);
  useEffect(() => {
    windowDimsRef.current = windowDimensions;
  }, [windowDimensions]);

  const handleCriticalError = useErrorHandler();
  const componentIsStillLoaded = useComponentIsStillLoaded();

  const [dimSwappedRef, setDimSwappedRef] = useLocalRef(
    LSKey.VIDEO_DIMENSIONS_SWAPPED,
    false
  );
  const [scanOffsetsRef, setScanOffsetsRef] = useLocalRef(
    LSKey.SCAN_WINDOW_OFFSETS,
    getScanOffsetsFromTargetDims(
      windowDimensions,
      dimSwappedRef.current
    )
  );

  useSubscription(command$, {
    next: command => {
      if (command === 'pause') videoRef.current?.pause();
      if (command === 'play') {
        videoRef.current?.play().catch(logSentry);
      }
    },
  });

  // If scanOffsets has already been matched, Quagga is unlikely to
  // be re-initialised, so reveal the video as soon as it loads data
  // (this occurs before Quagga.start is called, so is faster)
  useEffectOnce(() => {
    const quickLoad = () => {
      if (scanOffsetsRef.current.hasMatched) setLoading(false);
    };
    // NOTE: copying videoRef.current here, as it likely will have
    // changed by the time cleanup happens
    const video = videoRef.current;
    video?.addEventListener('loadeddata', quickLoad);
    return () => video?.removeEventListener('loadeddata', quickLoad);
  });

  const checkDecodeResult = useCallback(
    (result: QuaggaJSResultObject) => {
      if (quaggaResultIsValid(result)) {
        onDataUpdate(result.codeResult);
      }
    },
    [onDataUpdate]
  );

  const getQuaggaConfig = useCallback(
    (): QuaggaJSConfigObject => ({
      inputStream: {
        type: 'LiveStream',
        constraints: {
          facingMode: 'environment',
          ...getTargetDims(!!dimSwappedRef.current),
        },
        target: targetRef.current ?? undefined,
        area: _.mapValues(getOffsets(scanOffsetsRef), n => `${n}%`),
      },
      locate: false,
      numOfWorkers: window.navigator?.hardwareConcurrency ?? 0,
      decoder: { readers: barcodeTypes },
    }),
    [barcodeTypes, dimSwappedRef, scanOffsetsRef]
  );

  useEffectOnce(() => {
    initializeQuagga({
      getQuaggaConfig,
      windowDimsRef,
      scanOffsetsRef,
      setScanOffsetsRef,
      dimSwappedRef,
      setDimSwappedRef,
      setLoading,
      componentIsStillLoaded,
      onStreamError: (e: unknown) => {
        onStreamError?.(e);
        setVideoError(true);
      },
      handleCriticalError,
    });

    Quagga.onDetected(checkDecodeResult);
    return () => {
      Quagga.offDetected(checkDecodeResult);
      Quagga.stop().catch(handleCriticalError);
    };
  });

  return (
    <>
      <TargetDiv
        ref={targetRef}
        $size={windowDimensions}
        $ready={!loading}
        {...{ className }}
      >
        <video muted ref={videoRef} />
      </TargetDiv>
      <RedScanLine $scanWSize={windowDimensions.scanWindowSize} />
      <CameraLoading
        show={loading || videoError}
        scale={windowDimensions.scanWindowSize / 400}
        state={
          videoError ? 'error' : slowLoading ? 'loading' : 'static'
        }
      />
    </>
  );
}
